/**
 * AiChatMessage — Renders a single chat message (user, assistant, or system).
 *
 * Assistant messages contain interleaved text segments and tool call blocks.
 * User messages are compact right-aligned bubbles.
 */

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, Bot, Info } from 'lucide-react';
import type { AiChatMessage as AiChatMessageType } from '../types';
import { AiChatToolCallBlock } from './AiChatToolCallBlock';
import { AiChatThinkingBlock } from './AiChatThinkingBlock';

type AiChatMessageProps = {
  message: AiChatMessageType;
  statusText?: string | null;
};

/** Extract a human-readable string from content that may be a string, JSON string, or object. */
function displayContent(content: unknown): string {
  if (typeof content !== 'string') {
    return extractErrorFromObject(content) ?? JSON.stringify(content);
  }
  // Check if string is a JSON error response like {"error":{"message":"..."}}
  if (content.startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      const msg = extractErrorFromObject(parsed);
      if (msg) return msg;
    } catch { /* not JSON, use as-is */ }
  }
  return content;
}

function extractErrorFromObject(obj: unknown): string | null {
  if (!obj || typeof obj !== 'object') return null;
  const record = obj as Record<string, unknown>;
  if (typeof record.message === 'string') return record.message;
  if (record.error && typeof record.error === 'object') {
    const err = record.error as Record<string, unknown>;
    if (typeof err.message === 'string') return err.message;
  }
  return null;
}

export function AiChatMessage({ message, statusText }: AiChatMessageProps) {
  if (message.role === 'system') {
    return (
      <div className="flex items-start gap-2 px-3 py-2 text-xs text-muted-foreground bg-muted/30 rounded-md" data-testid="chat-message-system">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>{displayContent(message.content)}</span>
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="chat-message-user">
        <div className="flex items-start gap-2 max-w-[85%]">
          <div className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm">
            {message.content}
          </div>
          <div className="shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center mt-0.5">
            <User className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>
    );
  }

  // Assistant message — mixed content (text + tool calls)
  return (
    <div className="flex items-start gap-2" data-testid="chat-message-assistant">
      <div className="shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
        <Bot className="h-3.5 w-3.5 text-primary" />
      </div>
      <div className="flex-1 min-w-0 text-sm">
        {message.segments.map((seg, i) => {
          if (seg.type === 'text') {
            return (
              <div key={i} className="prose prose-sm dark:prose-invert max-w-none [&_pre]:text-xs [&_code]:text-xs">
                <Markdown remarkPlugins={[remarkGfm]}>
                  {seg.text}
                </Markdown>
              </div>
            );
          }
          if (seg.type === 'thinking') {
            return (
              <AiChatThinkingBlock
                key={i}
                thinking={seg.thinking}
                isStreaming={seg.isStreaming}
              />
            );
          }
          return <AiChatToolCallBlock key={seg.toolCall.id} toolCall={seg.toolCall} />;
        })}
        {statusText && (
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs mt-1" data-testid="ai-chat-loading">
            <span className="animate-pulse">{statusText}</span>
            <span className="animate-bounce [animation-delay:100ms]">.</span>
            <span className="animate-bounce [animation-delay:200ms]">.</span>
            <span className="animate-bounce [animation-delay:300ms]">.</span>
          </div>
        )}
      </div>
    </div>
  );
}
